import React, { useState } from 'react';
import { FaSearch, FaExclamationCircle, FaCheckCircle, FaFileInvoiceDollar } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';
import { PublicService } from '../../../../application/services/PublicService';

export const ConsultasView = ({ formatCurrency }) => {
  const [codigoMedidor, setCodigoMedidor] = useState("");
  const [cedulaCliente, setCedulaCliente] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");

  const handleConsultar = async (e) => {
    e.preventDefault();
    setError("");
    setResultado(null);
    setLoading(true);

    try {
      const data = await PublicService.consultarDeuda(
        codigoMedidor,
        cedulaCliente,
      );
      setResultado(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-32 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="bg-blue-600 p-8 text-white text-center">
          <h2 className="text-3xl font-bold">
            Consulta de Consumo y Pagos
          </h2>
          <p className="text-blue-100 mt-2">
            Ingrese sus datos para verificar deudas pendientes
          </p>
        </div>
        <div className="p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <form onSubmit={handleConsultar} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-700">
                    Código de Medidor:
                  </label>
                  <input
                    type="text"
                    required
                    value={codigoMedidor}
                    onChange={(e) => setCodigoMedidor(e.target.value)}
                    placeholder="Ingrese código"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 focus:border-blue-500 rounded-xl outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-700">
                    Cédula del Cliente:
                  </label>
                  <input
                    type="text"
                    required
                    value={cedulaCliente}
                    onChange={(e) => setCedulaCliente(e.target.value)}
                    placeholder="Ingrese cédula"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 focus:border-blue-500 rounded-xl outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:bg-blue-300"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      <FaSearch /> Consultar Deuda
                    </>
                  )}
                </button>
              </form>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 flex items-center gap-3 font-medium">
                  <FaExclamationCircle /> {error}
                </div>
              )}

              {/* Resultados para Móvil - Aparecen justo debajo del botón */}
              {resultado && (
                <div className="lg:hidden animate-fade-in space-y-6">
                  <div className="border-b border-gray-200 pb-4 flex justify-between items-end">
                    <div>
                      <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest">
                        Estado de Cuenta
                      </h3>
                      <h4 className="text-xl font-bold text-gray-800">
                        {resultado.cliente}
                      </h4>
                    </div>
                  </div>
                  {resultado.deudas.length === 0 ? (
                    <div className="bg-green-100 text-green-700 p-6 rounded-2xl text-center font-bold">
                      ¡Felicidades! Usted se encuentra al día con sus
                      pagos.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {resultado.deudas.map((deuda, idx) => (
                        <div
                          key={idx}
                          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100"
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                                {deuda.descripcion}
                              </p>
                              <p className="text-lg font-black text-gray-900 capitalize">
                                {deuda.mes}
                              </p>
                            </div>
                            <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-black uppercase border border-red-200">
                              Pendiente
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 py-3 border-y border-gray-50">
                            <div className="space-y-0.5">
                              <p className="text-[9px] font-bold text-gray-400 uppercase">
                                Base
                              </p>
                              <p className="text-xs font-bold text-gray-700">
                                {formatCurrency(deuda.valores.base)}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-[9px] font-bold text-gray-400 uppercase">
                                Exceso
                              </p>
                              <p className="text-xs font-bold text-amber-600">
                                {formatCurrency(
                                  deuda.valores.valorExceso,
                                )}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-[9px] font-bold text-gray-400 uppercase">
                                Mora
                              </p>
                              <p className="text-xs font-bold text-red-500">
                                {formatCurrency(deuda.valores.mora)}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-[9px] font-bold text-gray-400 uppercase">
                                Otros
                              </p>
                              <p className="text-xs font-bold text-blue-500">
                                {formatCurrency(
                                  deuda.valores.reconexion +
                                    (deuda.valores.deuda_extra || 0),
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 flex justify-between items-center text-xs">
                            <span className="text-gray-400 font-bold uppercase tracking-tighter">
                              Lectura: {deuda.lecturas.actual}
                            </span>
                            <p className="text-xl font-black text-blue-700">
                              {formatCurrency(deuda.valores.total)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div
                className={`bg-yellow-50 p-6 rounded-2xl border border-yellow-100 transition-all ${resultado ? "hidden lg:block" : ""}`}
              >
                <h4 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                  <FaCheckCircle /> Ayuda en la Consulta
                </h4>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  Si no conoce su código de medidor, puede encontrarlo en
                  su última factura física.
                </p>
              </div>
            </div>

            <div className="hidden lg:block bg-gray-50 rounded-3xl p-6 lg:p-8 min-h-[300px] border border-gray-100">
              {!resultado && !loading && (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 text-center">
                  <FaFileInvoiceDollar
                    size={64}
                    className="mb-4 opacity-20"
                  />
                  <p className="font-medium">Esperando consulta...</p>
                </div>
              )}
              {resultado && (
                <div className="space-y-6">
                  <div className="border-b border-gray-200 pb-4 flex justify-between items-end">
                    <div>
                      <h3 className="text-sm font-bold text-blue-600 uppercase tracking-widest">
                        Estado de Cuenta
                      </h3>
                      <h4 className="text-2xl font-bold text-gray-800">
                        {resultado.cliente}
                      </h4>
                    </div>
                  </div>
                  {resultado.deudas.length === 0 ? (
                    <div className="bg-green-100 text-green-700 p-8 rounded-2xl text-center font-bold text-lg">
                      ¡Felicidades! Usted se encuentra al día con sus
                      pagos.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {resultado.deudas.map((deuda, idx) => (
                        <div
                          key={idx}
                          className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 group hover:border-blue-200 transition-all"
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                                {deuda.descripcion}
                              </p>
                              <p className="text-xl font-black text-gray-900 capitalize">
                                {deuda.mes}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-[10px] bg-red-100 text-red-600 px-3 py-1 rounded-full font-black uppercase tracking-tighter shadow-sm border border-red-200">
                                Pendiente
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4 border-y border-gray-50 mt-2">
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                Agua Base
                              </p>
                              <p className="text-sm font-bold text-gray-700">
                                {formatCurrency(deuda.valores.base)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                Exceso
                              </p>
                              <p className="text-sm font-bold text-amber-600">
                                {formatCurrency(
                                  deuda.valores.valorExceso,
                                )}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                Mora
                              </p>
                              <p className="text-sm font-bold text-red-500">
                                {formatCurrency(deuda.valores.mora)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                Otros/Rec.
                              </p>
                              <p className="text-sm font-bold text-blue-500">
                                {formatCurrency(
                                  deuda.valores.reconexion +
                                    (deuda.valores.deuda_extra || 0),
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex justify-between items-center bg-blue-50/50 p-4 rounded-xl">
                            <div className="flex gap-4 text-[10px] text-gray-500 font-bold uppercase tracking-tight">
                              <span>
                                Lectura: {deuda.lecturas.actual}
                              </span>
                              <span>Consumo: {deuda.consumos.m3}m³</span>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-black text-blue-700">
                                {formatCurrency(deuda.valores.total)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
